import { useConnection } from "@solana/wallet-adapter-react";
import { convert } from "@stepfinance/common";
import type { StakingPosition } from "@stepfinance/staking";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useMarkets } from "../../contexts/market";
import { BONFIDA_STAKING_PROGRAM_ID, FIDA_MINT } from "../../models/bonfida/types";
import { StakeAccount } from "../../utils/bonfida/staking/staking";
import { getTokenByMint } from "../../utils/tokenInfo";

export const useBonfidaStaking = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const [bonfidaStaking, setBonfidaStaking] = useState<StakingPosition[]>([]);

  useEffect(() => setBonfidaStaking([]), [connected]);

  useEffect(() => {
    if (connected && marketsInitialised && publicKey) {
      void (async () => {
        const stakeAccounts = await StakeAccount.retrieveStakeAccountsForOwner(
          connection,
          publicKey,
          BONFIDA_STAKING_PROGRAM_ID,
        );

        const fidaTokenInfo = getTokenByMint(FIDA_MINT.toBase58());
        const fidaPriceUSD = priceInUSD(FIDA_MINT.toBase58()) ?? 0;

        const stakedPositions: StakingPosition[] = stakeAccounts.map((acc) => {
          const stakedAmount = convert(acc.stakeAccount.stakeAmount, fidaTokenInfo?.decimals);
          const valueInUSD = stakedAmount * fidaPriceUSD;

          return {
            kind: "Bonfida" as const,
            link: "https://staking.bonfida.org/",
            apy: null,
            stakingMint: FIDA_MINT,
            amountStaked: stakedAmount,
            rewardMint: FIDA_MINT,
            pendingReward: null,
            pendingRewardUSD: null,
            valueInUSD,
          };
        });

        setBonfidaStaking(stakedPositions.filter((x): x is StakingPosition => !!x));
      })();
    }
  }, [connected, connection, publicKey, marketsInitialised, priceInUSD]);
  return bonfidaStaking;
};
